import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { useNavigate, Link } from 'react-router-dom';
import {
  ArrowRight, CheckCircle, Star, Users, BookOpen, MessageSquare,
  Brain, Trophy, BarChart3, Target, Zap,
  ChevronRight, Menu, X, Mic, Heart, Shield, Sparkles
} from 'lucide-react';
import { Button } from '@/components/ui/button';

const YAS_LOGO = '/yas-logo.png';
const HERO_IMG = 'https://images.unsplash.com/photo-1758272133833-3a2277d426e4?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDQ2Mzl8MHwxfHNlYXJjaHw0fHxkaXZlcnNlJTIwdXJiYW4lMjB5b3V0aCUyMGdyb3VwJTIwc21pbGluZ3xlbnwwfHx8fDE3ODQ0Mjc5MjF8MA&ixlib=rb-4.1.0&q=85&w=1200';
const WORKSHOP_IMG = 'https://images.pexels.com/photos/5212697/pexels-photo-5212697.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940';

const NAV_LINKS = [
  { label: 'About', path: '/about' },
  { label: 'AI Features', path: '/ai' },
  { label: 'Programs', path: '/programs' },
  { label: 'Contact', path: '/contact' },
  { label: 'Request Demo', path: '/demo' },
];

const fadeIn = { initial: { opacity: 0, y: 20 }, whileInView: { opacity: 1, y: 0 }, viewport: { once: true }, transition: { duration: 0.5 } };

// Shared nav — warm design
export const SiteNav = () => {
  const navigate = useNavigate();
  const [mobileOpen, setMobileOpen] = useState(false);
  return (
    <nav className="sticky top-0 z-50 border-b" style={{ backgroundColor: 'rgba(245,243,239,0.92)', backdropFilter: 'blur(16px)', borderColor: 'rgba(45,40,36,0.08)' }}>
      <div className="max-w-6xl mx-auto px-6 py-3 flex items-center justify-between">
        <div className="flex items-center gap-2 cursor-pointer" onClick={() => navigate('/')}>
          <img src={YAS_LOGO} alt="YAS Connect" className="w-12 h-12" />
          <span style={{ fontFamily: "'Bricolage Grotesque', sans-serif" }} className="text-lg font-bold text-[#2D2824]">YAS Connect</span>
        </div>
        <div className="hidden md:flex items-center gap-6">
          {NAV_LINKS.map(l => (
            <Link key={l.path} to={l.path} style={{ fontFamily: "'Manrope', sans-serif", transition: 'color 0.2s ease' }}
              className="text-sm font-medium text-[#2D2824]/60 hover:text-[#E86C3D]">{l.label}</Link>
          ))}
          <Button onClick={() => navigate('/login')} className="bg-[#E86C3D] hover:bg-[#D55A2D] text-white rounded-full px-5 text-sm font-bold" style={{ transition: 'background 0.2s ease' }}>
            Log In
          </Button>
        </div>
        <button className="md:hidden p-2" onClick={() => setMobileOpen(!mobileOpen)}>
          {mobileOpen ? <X className="w-6 h-6 text-[#2D2824]" /> : <Menu className="w-6 h-6 text-[#2D2824]" />}
        </button>
      </div>
      {mobileOpen && (
        <div className="md:hidden px-6 py-4 space-y-3 bg-white border-t border-[#2D2824]/5">
          {NAV_LINKS.map(l => (
            <Link key={l.path} to={l.path} onClick={() => setMobileOpen(false)} className="block text-sm font-medium text-[#2D2824]/70 hover:text-[#E86C3D]">{l.label}</Link>
          ))}
          <Button onClick={() => { navigate('/login'); setMobileOpen(false); }} className="w-full bg-[#E86C3D] hover:bg-[#D55A2D] text-white rounded-full text-sm font-bold">Log In</Button>
        </div>
      )}
    </nav>
  );
};

// Shared footer — warm design
export const SiteFooter = () => {
  return (
    <div style={{ backgroundColor: '#2D2824' }} className="py-12 px-6">
      <div className="max-w-6xl mx-auto">
        <div className="grid md:grid-cols-4 gap-8 text-sm mb-8">
          <div>
            <div className="flex items-center gap-2 mb-4">
              <img src={YAS_LOGO} alt="" className="w-8 h-8 rounded-lg bg-white p-0.5" />
              <span style={{ fontFamily: "'Bricolage Grotesque', sans-serif", color: 'white' }} className="font-bold">YAS Connect</span>
            </div>
            <p style={{ color: 'rgba(255,255,255,0.5)' }} className="text-xs leading-relaxed">AI-powered youth development platform for employment programs, school districts, and youth organizations.</p>
          </div>
          <div>
            <p className="font-bold text-xs mb-3 tracking-[0.1em] uppercase" style={{ color: 'rgba(255,255,255,0.4)' }}>Platform</p>
            {[{ l: 'AI Features', p: '/ai' }, { l: 'Programs', p: '/programs' }, { l: 'How It Works', p: '/how-it-works' }].map(l => (
              <Link key={l.p} to={l.p} className="block text-xs mb-2 hover:text-[#E86C3D]" style={{ color: 'rgba(255,255,255,0.6)', transition: 'color 0.2s ease' }}>{l.l}</Link>
            ))}
          </div>
          <div>
            <p className="font-bold text-xs mb-3 tracking-[0.1em] uppercase" style={{ color: 'rgba(255,255,255,0.4)' }}>Resources</p>
            {[{ l: 'Youth Guide', p: '/guide' }, { l: 'Request Demo', p: '/demo' }, { l: 'For Staff', p: '/tutorial/staff' }].map(l => (
              <Link key={l.p} to={l.p} className="block text-xs mb-2 hover:text-[#E86C3D]" style={{ color: 'rgba(255,255,255,0.6)', transition: 'color 0.2s ease' }}>{l.l}</Link>
            ))}
          </div>
          <div>
            <p className="font-bold text-xs mb-3 tracking-[0.1em] uppercase" style={{ color: 'rgba(255,255,255,0.4)' }}>Contact</p>
            <p style={{ color: 'rgba(255,255,255,0.6)' }} className="text-xs mb-1">Newark, NJ</p>
            <p style={{ color: 'rgba(255,255,255,0.6)' }} className="text-xs">info@yasconnect.co</p>
          </div>
        </div>
        <div className="border-t border-white/10 pt-6 text-center">
          <p style={{ color: 'rgba(255,255,255,0.3)' }} className="text-xs">&copy; 2026 YAS Connect. All rights reserved.</p>
        </div>
      </div>
    </div>
  );
};

const HomePage = () => {
  const navigate = useNavigate();

  return (
    <div style={{ backgroundColor: '#F5F3EF', fontFamily: "'Manrope', sans-serif" }}>
      <SiteNav />

      {/* Hero */}
      <div className="max-w-6xl mx-auto px-6 py-16 md:py-24">
        <div className="grid md:grid-cols-2 gap-12 items-center">
          <motion.div initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }}>
            <span className="text-[#E86C3D] text-xs font-bold tracking-[0.2em] uppercase mb-4 block">Youth Development Platform</span>
            <h1 style={{ fontFamily: "'Bricolage Grotesque', sans-serif" }} className="text-4xl md:text-6xl font-extrabold tracking-tighter leading-none text-[#2D2824] mb-6">
              Where Young People<br/>
              <span className="text-[#E86C3D]">Grow, Connect</span><br/>
              & Thrive
            </h1>
            <p className="text-base md:text-lg text-[#2D2824]/60 leading-relaxed mb-8 max-w-md">
              The AI-powered platform that helps youth programs track growth, explore careers, 
              and prove impact — while giving young people a digital home for their development journey.
            </p>
            <div className="flex flex-wrap gap-3 mb-8">
              <Button onClick={() => navigate('/demo')} className="bg-[#E86C3D] hover:bg-[#D55A2D] text-white rounded-full px-7 py-3 text-base font-bold shadow-lg" style={{ transition: 'background 0.2s ease' }}>
                Request a Demo <ArrowRight className="w-4 h-4 ml-2" />
              </Button>
              <Button onClick={() => navigate('/ai')} className="rounded-full px-7 py-3 text-base font-bold bg-[#2D2824]/5 text-[#2D2824] hover:bg-[#2D2824]/10 border border-[#2D2824]/10" style={{ transition: 'background 0.2s ease' }}>
                See AI Features
              </Button>
            </div>
            <div className="flex flex-wrap gap-4 text-sm text-[#2D2824]/50">
              <span className="flex items-center gap-1.5"><CheckCircle className="w-4 h-4 text-[#4ADE80]" /> Trusted by youth programs</span>
              <span className="flex items-center gap-1.5"><CheckCircle className="w-4 h-4 text-[#4ADE80]" /> 14 AI features</span>
              <span className="flex items-center gap-1.5"><CheckCircle className="w-4 h-4 text-[#4ADE80]" /> Voice-enabled</span>
            </div>
          </motion.div>
          <motion.div initial={{ opacity: 0, scale: 0.95 }} animate={{ opacity: 1, scale: 1 }} transition={{ delay: 0.3 }}
            className="hidden md:block">
            <div className="rounded-3xl overflow-hidden shadow-2xl rotate-1 hover:rotate-0" style={{ transition: 'transform 0.3s ease' }}>
              <img src={HERO_IMG} alt="Youth community" className="w-full h-96 object-cover" />
            </div>
          </motion.div>
        </div>
      </div>

      {/* What We Do */}
      <div className="bg-white py-20 px-6">
        <div className="max-w-6xl mx-auto">
          <motion.div {...fadeIn} className="text-center mb-14">
            <span className="text-[#E86C3D] text-xs font-bold tracking-[0.2em] uppercase">What We Do</span>
            <h2 style={{ fontFamily: "'Bricolage Grotesque', sans-serif" }} className="text-3xl md:text-5xl font-extrabold tracking-tight text-[#2D2824] mt-3 mb-4">
              Everything Your Program Needs
            </h2>
            <p className="text-[#2D2824]/60 max-w-2xl mx-auto">One platform that replaces paper workbooks, spreadsheets, and fragmented communication — powered by AI that actually understands each youth.</p>
          </motion.div>

          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
            {[
              { icon: Brain, title: 'AI Career Coach', desc: '24/7 career advisor that knows each youth\'s interests, skills, and goals — with voice input so they can talk, not type.', color: '#8B5CF6' },
              { icon: Trophy, title: 'Wins Wall', desc: 'The social hub where youth celebrate achievements, hype each other up, and build community. Think professional highlight reel.', color: '#FFD60A' },
              { icon: Target, title: 'SMART Goals', desc: 'Youth set goals with AI assistance. Track progress, earn points, and auto-celebrate on the Wins Wall when complete.', color: '#4ADE80' },
              { icon: BookOpen, title: '13 Courses', desc: 'Financial literacy, leadership, communication, entrepreneurship — interactive courses with real-world skills.', color: '#EC4899' },
              { icon: Sparkles, title: '6 Impact Projects', desc: 'Interactive workbooks where youth build something real — a business plan, content channel, or community project.', color: '#6366F1' },
              { icon: BarChart3, title: 'Visual Analytics', desc: '8 interactive dashboards showing engagement, wellness, worksites, staff activity — with AI-generated executive summaries.', color: '#E86C3D' },
              { icon: MessageSquare, title: 'Smart Q&A', desc: 'Youth ask questions, monitors get instant alerts. AI suggests responses. Share answers with the whole cohort in one click.', color: '#3B82F6' },
              { icon: Mic, title: 'Voice Input', desc: 'Youth can speak their goals, wins, check-ins, and chat with AI using their voice. We meet them where they are.', color: '#F59E0B' },
              { icon: Shield, title: 'Data & Reports', desc: 'PDF reports for funders, CSV exports for analysis, completion tracking per youth, and AI-enhanced impact summaries.', color: '#14B8A6' },
            ].map((f, i) => (
              <motion.div key={i} {...fadeIn} transition={{ delay: i * 0.05 }}
                className="p-6 rounded-3xl border border-black/5 bg-white hover:-translate-y-1 hover:shadow-lg group"
                style={{ transition: 'transform 0.2s ease, box-shadow 0.2s ease' }}>
                <div className="w-12 h-12 rounded-2xl flex items-center justify-center mb-4" style={{ backgroundColor: f.color + '15' }}>
                  <f.icon className="w-6 h-6" style={{ color: f.color }} />
                </div>
                <h3 style={{ fontFamily: "'Bricolage Grotesque', sans-serif" }} className="text-lg font-bold text-[#2D2824] mb-2">{f.title}</h3>
                <p className="text-sm text-[#2D2824]/55 leading-relaxed">{f.desc}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </div>

      {/* Who It's For */}
      <div className="max-w-6xl mx-auto px-6 py-20">
        <motion.div {...fadeIn} className="text-center mb-14">
          <span className="text-[#4ADE80] text-xs font-bold tracking-[0.2em] uppercase">Who It's For</span>
          <h2 style={{ fontFamily: "'Bricolage Grotesque', sans-serif" }} className="text-3xl md:text-5xl font-extrabold tracking-tight text-[#2D2824] mt-3">
            Built for Every Youth Program
          </h2>
        </motion.div>
        <div className="grid md:grid-cols-3 gap-6">
          {[
            { title: 'Summer Employment (SYEP)', desc: 'Track worksites, monitor cohorts, weekly check-ins, assessments, and career exploration for summer job programs.', color: '#E86C3D', features: ['Worksite analytics', 'Lead monitor tools', 'Weekly check-ins'] },
            { title: 'School Districts (GPS)', desc: 'Career exploration, post-secondary planning, counselor dashboards, and academic-year engagement for high schools.', color: '#3B82F6', features: ['Career explorer', 'Counselor dashboard', 'College planning'] },
            { title: 'Youth Organizations', desc: 'Development programs, leadership academies, workforce training — fully configurable features for any program type.', color: '#8B5CF6', features: ['Flexible features', 'Custom branding', 'Multi-program'] },
          ].map((p, i) => (
            <motion.div key={i} {...fadeIn} transition={{ delay: i * 0.1 }}
              className="p-8 rounded-3xl bg-white border border-black/5 hover:-translate-y-1 hover:shadow-lg" style={{ transition: 'transform 0.2s ease, box-shadow 0.2s ease' }}>
              <div className="w-3 h-12 rounded-full mb-5" style={{ backgroundColor: p.color }} />
              <h3 style={{ fontFamily: "'Bricolage Grotesque', sans-serif" }} className="text-xl font-bold text-[#2D2824] mb-3">{p.title}</h3>
              <p className="text-sm text-[#2D2824]/55 leading-relaxed mb-4">{p.desc}</p>
              <div className="space-y-2">
                {p.features.map((f, j) => (
                  <div key={j} className="flex items-center gap-2 text-sm text-[#2D2824]/70">
                    <CheckCircle className="w-4 h-4 flex-shrink-0" style={{ color: p.color }} /> {f}
                  </div>
                ))}
              </div>
            </motion.div>
          ))}
        </div>
      </div>

      {/* Social Proof */}
      <div className="bg-[#2D2824] py-20 px-6">
        <div className="max-w-5xl mx-auto text-center">
          <motion.div {...fadeIn}>
            <h2 style={{ fontFamily: "'Bricolage Grotesque', sans-serif", color: 'white' }} className="text-3xl md:text-4xl font-extrabold tracking-tight mb-12">
              What People Are Saying
            </h2>
          </motion.div>
          <div className="grid md:grid-cols-3 gap-6">
            {[
              { name: 'Program Director', org: 'Youth Employment Program', text: 'This platform replaced our paper workbooks, spreadsheet tracking, and email chains — all in one place. The AI insights are a game changer for our funders.' },
              { name: 'Lead Monitor', org: 'Summer Program', text: 'I used to spend 30 minutes before every meeting trying to remember what each youth was working on. Now I click their card and know everything in 5 seconds.' },
              { name: 'Youth Participant', org: 'SYEP Program', text: 'The AI career coach suggested careers I never knew existed. And the Win Wall is where everyone goes to show what they accomplished — it\'s actually fun.' },
            ].map((t, i) => (
              <motion.div key={i} {...fadeIn} transition={{ delay: i * 0.1 }}
                className="p-6 rounded-3xl bg-white/8 backdrop-blur border border-white/10 text-left">
                <div className="flex mb-3">
                  {[...Array(5)].map((_, j) => <Star key={j} className="w-4 h-4 fill-[#FFD60A] text-[#FFD60A]" />)}
                </div>
                <p className="text-sm leading-relaxed mb-4" style={{ color: 'rgba(255,255,255,0.8)' }}>"{t.text}"</p>
                <div>
                  <p className="text-sm font-bold" style={{ color: 'white' }}>{t.name}</p>
                  <p className="text-xs" style={{ color: 'rgba(255,255,255,0.5)' }}>{t.org}</p>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </div>

      {/* CTA */}
      <div className="max-w-4xl mx-auto px-6 py-20 text-center">
        <motion.div {...fadeIn}>
          <h2 style={{ fontFamily: "'Bricolage Grotesque', sans-serif" }} className="text-3xl md:text-5xl font-extrabold tracking-tight text-[#2D2824] mb-4">
            Ready to Transform<br/>Your Program?
          </h2>
          <p className="text-[#2D2824]/60 text-lg mb-8 max-w-xl mx-auto">
            Join the growing number of organizations using YAS Connect to engage youth, track outcomes, and prove impact like never before.
          </p>
          <div className="flex flex-wrap gap-3 justify-center">
            <Button onClick={() => navigate('/demo')} className="bg-[#E86C3D] hover:bg-[#D55A2D] text-white rounded-full px-8 py-3 text-lg font-bold shadow-lg" style={{ transition: 'background 0.2s ease' }}>
              Request a Demo <ArrowRight className="w-5 h-5 ml-2" />
            </Button>
            <Button onClick={() => navigate('/ai')} className="rounded-full px-8 py-3 text-lg font-bold bg-[#2D2824]/5 text-[#2D2824] hover:bg-[#2D2824]/10 border border-[#2D2824]/10" style={{ transition: 'background 0.2s ease' }}>
              Explore AI Features
            </Button>
          </div>
        </motion.div>
      </div>

      {/* Footer */}
      <div style={{ backgroundColor: '#2D2824' }} className="py-12 px-6">
        <div className="max-w-6xl mx-auto">
          <div className="grid md:grid-cols-4 gap-8 text-sm mb-8">
            <div>
              <div className="flex items-center gap-2 mb-4">
                <img src={YAS_LOGO} alt="" className="w-8 h-8 rounded-lg bg-white p-0.5" />
                <span style={{ fontFamily: "'Bricolage Grotesque', sans-serif", color: 'white' }} className="font-bold">YAS Connect</span>
              </div>
              <p style={{ color: 'rgba(255,255,255,0.5)' }} className="text-xs leading-relaxed">AI-powered youth development platform for employment programs, school districts, and youth organizations.</p>
            </div>
            <div>
              <p className="font-bold text-xs mb-3 tracking-[0.1em] uppercase" style={{ color: 'rgba(255,255,255,0.4)' }}>Platform</p>
              {[{ l: 'AI Features', p: '/ai' }, { l: 'Programs', p: '/programs' }, { l: 'How It Works', p: '/how-it-works' }].map(l => (
                <Link key={l.p} to={l.p} className="block text-xs mb-2 hover:text-[#E86C3D]" style={{ color: 'rgba(255,255,255,0.6)', transition: 'color 0.2s ease' }}>{l.l}</Link>
              ))}
            </div>
            <div>
              <p className="font-bold text-xs mb-3 tracking-[0.1em] uppercase" style={{ color: 'rgba(255,255,255,0.4)' }}>Resources</p>
              {[{ l: 'Youth Guide', p: '/guide' }, { l: 'Request Demo', p: '/demo' }, { l: 'For Staff', p: '/tutorial/staff' }].map(l => (
                <Link key={l.p} to={l.p} className="block text-xs mb-2 hover:text-[#E86C3D]" style={{ color: 'rgba(255,255,255,0.6)', transition: 'color 0.2s ease' }}>{l.l}</Link>
              ))}
            </div>
            <div>
              <p className="font-bold text-xs mb-3 tracking-[0.1em] uppercase" style={{ color: 'rgba(255,255,255,0.4)' }}>Contact</p>
              <p style={{ color: 'rgba(255,255,255,0.6)' }} className="text-xs mb-1">Newark, NJ</p>
              <p style={{ color: 'rgba(255,255,255,0.6)' }} className="text-xs">info@yasconnect.co</p>
            </div>
          </div>
          <div className="border-t border-white/10 pt-6 text-center">
            <p style={{ color: 'rgba(255,255,255,0.3)' }} className="text-xs">&copy; 2026 YAS Connect. All rights reserved.</p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default HomePage;
